/**
 * features/registerFindings.ts — findings from registers we already ingest
 * =========================================================================
 * The LK4410 column map parses ~104 registers; most of the reactive-energy and
 * power-quality block was landing in `RawMeterRow` and never being read. These
 * three detectors close that gap. Each maps to one entry in the August 2026
 * feasibility assessment and each is deterministic on the installed meter —
 * no new sensing, no controller link.
 *
 *   standbyFloorFinding  → Feature 24, Standby / off-hours load
 *                          Works on EVERY meter model (needs only wattsTotal).
 *   leadingPfFinding     → Feature 22, Leading power-factor sentry
 *                          Needs varhCapReceived → LK4410 sites only.
 *   voltageThdFinding    → Feature 8, aggregate half only
 *                          Needs v*ThdPct → LK4410 sites only.
 *
 * Every detector returns null when the register it needs is absent or the
 * condition is not present, so callers can push the result unconditionally.
 *
 * DELIBERATELY FREE OF RUNTIME IMPORTS — type-only imports keep this module
 * loadable by `node --test` without Vite's `@/` alias resolver. Keep it so.
 */

import type { TelemetryRow } from '@/types';
import type { HealthFinding } from '@/lib/reports/buildWeeklyReportPayload';

/** A HealthFinding plus the rank the weekly report sorts candidates by. */
export type ScoredFinding = HealthFinding & { score: number };

// ─── Local helpers (no imports — see module note) ─────────────────────────────

const finite = (n: unknown): number =>
  typeof n === 'number' && Number.isFinite(n) ? n : 0;

/** Linear-interpolated percentile over an unsorted numeric array. */
function percentileOf(values: number[], p: number): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = (sorted.length - 1) * (p / 100);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

/**
 * Median gap between consecutive readings, in hours. Used to convert a sample
 * count into real elapsed time without assuming a fixed poll interval.
 * Clamped to [1 min, 1 h] so a data gap cannot inflate an energy figure.
 */
export function medianIntervalHours(rows: TelemetryRow[]): number {
  if (rows.length < 2) return 0;
  const ts = rows.map(r => r.timestamp.getTime()).sort((a, b) => a - b);
  const gaps: number[] = [];
  for (let i = 1; i < ts.length; i++) {
    const g = ts[i] - ts[i - 1];
    if (g > 0) gaps.push(g);
  }
  if (!gaps.length) return 0;
  const medMs = percentileOf(gaps, 50);
  return Math.min(1, Math.max(1 / 60, medMs / 3_600_000));
}

/**
 * Hours each reading stands for: the real gap to the NEXT reading, clamped to
 * the same [1 min, 1 h] bounds as medianIntervalHours(). The final reading has
 * no successor and takes the median.
 *
 * Replaces `count × medianInterval` for any "how much time did these readings
 * cover" question. That shortcut is wrong twice over: a median of 5.02 minutes
 * against a true 5.00 poll makes a complete 288-sample day total 24.08 hours —
 * a day cannot contain more than 24 — and it silently assumes every reading
 * represents the same span, which stops being true the moment the poll rate
 * changes or the gateway drops a batch.
 *
 * Summing real gaps is self-normalising: a full day lands on 24, and a day with
 * missing readings lands below it because that time genuinely was not observed.
 *
 * A reading's span is also cut off at the end of its own shift day, so the last
 * reading before the 06:00 boundary cannot lend time to the day it belongs to —
 * that is the difference between a day totalling 24.0 and 24.1.
 *
 * Input must be sorted ascending by timestamp.
 */
function perRowHours(sorted: TelemetryRow[]): number[] {
  const n = sorted.length;
  if (n === 0) return [];
  if (n === 1) return [0];

  const fallback = medianIntervalHours(sorted);
  const out = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    const t = sorted[i].timestamp.getTime();
    const next = i < n - 1 ? sorted[i + 1].timestamp.getTime() : t + fallback * 3_600_000;
    const gapH = (next - t) / 3_600_000;
    const span = gapH > 0 ? Math.min(1, Math.max(1 / 60, gapH)) : 0;
    // Clip last, and after the 1-minute floor: a reading a few seconds before
    // the boundary must not borrow a whole minute from the following day.
    out[i] = Math.min(span, (shiftDayEnd(sorted[i].timestamp) - t) / 3_600_000);
  }
  return out;
}

/**
 * Hours covered by the readings matching `pick` (all of them by default).
 *
 * Each reading is weighted by its real gap to the next, so a subset is measured
 * against the same clock as the whole — and a day can never total more than 24.
 */
function hoursWhere(rows: TelemetryRow[], pick: (r: TelemetryRow) => boolean = () => true): number {
  const sorted = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const hours = perRowHours(sorted);
  return sorted.reduce((sum, r, i) => sum + (pick(r) ? hours[i] : 0), 0);
}

// ─── Non-production windows ───────────────────────────────────────────────────

/** A recurring non-production window. `day` omitted = every day. */
export interface NonProdWindow {
  /** 0=Sun … 6=Sat. Omit to apply the hour range on every day. */
  day?: number;
  /** Inclusive start hour, 0–23. */
  fromHour: number;
  /** Exclusive end hour, 0–24. May wrap past midnight (e.g. 22 → 6). */
  toHour: number;
}

/**
 * Default when a site has not declared its windows: nights 22:00–06:00 plus all
 * of Sunday. A commissioning knob, not a constant — real plants run odd shifts,
 * and a wrong window turns genuine production into apparent standby waste.
 */
export const DEFAULT_NON_PROD_WINDOWS: NonProdWindow[] = [
  { fromHour: 22, toHour: 6 },
  { day: 0, fromHour: 0, toHour: 24 },
];

export function inNonProdWindow(ts: Date, windows: NonProdWindow[]): boolean {
  const hour = ts.getHours();
  const day = ts.getDay();
  return windows.some(w => {
    if (w.day !== undefined && w.day !== day) return false;
    return w.fromHour <= w.toHour
      ? hour >= w.fromHour && hour < w.toHour            // same-day range
      : hour >= w.fromHour || hour < w.toHour;           // wraps midnight
  });
}

// ─── Feature 24 — standby / off-hours load ────────────────────────────────────

export interface StandbyOptions {
  /** Declared non-production windows. Defaults to nights + Sunday. */
  nonProdWindows?: NonProdWindow[];
  /**
   * Agreed non-addressable base load (security lighting, cold storage, servers,
   * trickle charging). Without it the floor is reported but no saving is
   * claimed — we will not guess what is essential on someone's site.
   */
  pEssentialKw?: number;
  /** Energy rate for costing the addressable part. */
  rateRsPerUnit: number;
  /** Unit label for the rate, e.g. "kWh" or "kVAh". */
  rateUnit?: string;
  /** Minimum non-prod samples before a floor is trustworthy. */
  minSamples?: number;
}

/**
 * The P5 percentile of off-hours load is the floor the plant never drops below
 * when nothing should be running. P5 rather than the minimum so that transient
 * dips do not set the floor, and occasional out-of-hours production does not
 * either — the assessment calls for a rolling multi-week window, so feed this
 * as many weeks as are available rather than a single week.
 */
export function standbyFloorFinding(
  rows: TelemetryRow[],
  opts: StandbyOptions,
): ScoredFinding | null {
  const windows = opts.nonProdWindows ?? DEFAULT_NON_PROD_WINDOWS;
  const minSamples = opts.minSamples ?? 100;

  const offHours = rows.filter(r => !r.isDropout && inNonProdWindow(r.timestamp, windows));
  if (offHours.length < minSamples) return null;

  const kw = offHours.map(r => finite(r.kwTotal)).filter(v => v >= 0);
  if (!kw.length) return null;

  const floorKw = percentileOf(kw, 5);
  const peakKw = percentileOf(rows.map(r => finite(r.kwTotal)), 99);

  // A floor that is a rounding error against the site's own peak is not a
  // finding, it is noise.
  if (floorKw < 1 || (peakKw > 0 && floorKw / peakKw < 0.02)) return null;

  const offHoursTotal = hoursWhere(rows, r => !r.isDropout && inNonProdWindow(r.timestamp, windows));
  const unit = opts.rateUnit ?? 'kWh';

  const essential = opts.pEssentialKw;
  const addressableKw = essential !== undefined ? Math.max(0, floorKw - essential) : null;
  const addressableCost = addressableKw !== null
    ? addressableKw * offHoursTotal * opts.rateRsPerUnit
    : null;

  const r1 = (n: number) => n.toFixed(1);
  const r0 = (n: number) => Math.round(n).toLocaleString('en-IN');

  const explanation = addressableKw !== null
    ? `Across ${r1(offHoursTotal)} non-production hours this period the site never dropped below ${r1(floorKw)} kW. With ${r1(essential!)} kW agreed as essential base load, ${r1(addressableKw)} kW is addressable — about ₹${r0(addressableCost!)} of energy drawn while nothing was scheduled to run.`
    : `Across ${r1(offHoursTotal)} non-production hours this period the site never dropped below ${r1(floorKw)} kW, against a working peak of ${r1(peakKw)} kW. No essential base load has been agreed for this site yet, so the addressable share of that floor is not yet quantified.`;

  return {
    kind: 'standbyFloor',
    title: 'Load continuing through non-production hours',
    explanation,
    action: addressableKw !== null
      ? `Walk the plant during a declared non-production window and identify what is drawing ${r1(addressableKw)} kW above the agreed essential load — compressors left pressurised, idling drives, and lighting circuits are the usual sources. Confirm each item can be safely isolated before scheduling any switch-off.`
      : `Agree an essential base load with the site (security lighting, cold storage, servers, trickle charging), then walk a non-production window to identify what makes up the remainder of the ${r1(floorKw)} kW floor.`,
    stats: {
      'the off-hours load floor, 5th percentile (kW)': `${r1(floorKw)} kW`,
      'working peak load for comparison, 99th percentile (kW)': `${r1(peakKw)} kW`,
      'total non-production hours measured in this period': r1(offHoursTotal),
      'number of readings taken during non-production hours': String(offHours.length),
      ...(essential !== undefined ? {
        'agreed essential base load (kW)': `${r1(essential)} kW`,
        'addressable standby load above essential (kW)': `${r1(addressableKw!)} kW`,
        [`cost of that addressable load at ₹${opts.rateRsPerUnit}/${unit}`]: `₹${r0(addressableCost!)}`,
      } : {}),
    },
    narrativeFacts: [
      'The 5th percentile is used rather than the minimum so a momentary dip cannot set the floor, and so occasional out-of-hours production does not either.',
      ...(essential === undefined
        ? ['No essential base load has been agreed for this site, so do NOT present the whole floor as waste or as a saving — present it as the measured floor and ask for the essential figure.']
        : ['The addressable figure is the floor MINUS the agreed essential load, so it is already net of what must legitimately stay on.']),
      'Non-production windows are a site setting. If these hours are wrong for this plant, the figure is wrong — flag it rather than defending it.',
    ],
    score: 12 + Math.min(10, floorKw / Math.max(1, peakKw) * 100),
  };
}

// ─── Feature 22 — leading power-factor sentry ─────────────────────────────────

export interface LeadingPfOptions {
  /** Smallest switchable APFC stage (kVAr). Enables the mis-specification finding. */
  qStepMinKvar?: number;
  /** Share of inductive kVArh above which leading drift is worth reporting. */
  minShareOfInductive?: number;
}

/**
 * varh_cap and varh_ind are mutually exclusive quadrant accumulators, so on a
 * correctly compensated plant varh_cap sits frozen and only advances during
 * genuine leading excursions. Its rate of increase — especially overnight and
 * at weekends — is direct over-correction evidence needing no computation.
 *
 * The register accumulates NEGATIVE on this fleet (verified across the export:
 * min −31,095,764, max 0), hence the magnitude arithmetic. Increments are
 * summed step-by-step and negative steps ignored, so a counter reset or a
 * meter swap cannot manufacture a huge fake excursion.
 */
export function leadingPfFinding(
  rows: TelemetryRow[],
  opts: LeadingPfOptions = {},
): ScoredFinding | null {
  const present = rows.some(r => finite(r.varhCapReceived) !== 0);
  if (!present) return null;   // LK4405 site — register not reported

  const sorted = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const windows = DEFAULT_NON_PROD_WINDOWS;

  let capKvarh = 0;
  let capOffHoursKvarh = 0;
  let indKvarh = 0;

  for (let i = 1; i < sorted.length; i++) {
    const dCap = cumulativeStep(sorted[i - 1].varhCapReceived, sorted[i].varhCapReceived);
    const dInd = cumulativeStep(sorted[i - 1].varhIndReceived, sorted[i].varhIndReceived);
    if (dCap !== null) {
      capKvarh += dCap;
      if (inNonProdWindow(sorted[i].timestamp, windows)) capOffHoursKvarh += dCap;
    }
    if (dInd !== null) indKvarh += dInd;
  }

  capKvarh /= 1000;
  capOffHoursKvarh /= 1000;
  indKvarh /= 1000;

  if (capKvarh <= 0) return null;

  const minShare = opts.minShareOfInductive ?? 0.02;
  const share = indKvarh > 0 ? capKvarh / indKvarh : 1;
  if (share < minShare) return null;   // trace leading, not over-correction

  const offHoursShare = capKvarh > 0 ? capOffHoursKvarh / capKvarh : 0;
  const maxVll = Math.max(0, ...rows.map(r => finite(r.vllMax)), ...rows.map(r => finite(r.vllAvg)));

  const r1 = (n: number) => n.toFixed(1);
  const pct = (n: number) => `${(n * 100).toFixed(0)}%`;

  // The design finding: a surplus smaller than the smallest switchable stage
  // cannot be corrected by switching at all — the bank is mis-specified.
  const undersizedStage = opts.qStepMinKvar !== undefined && capKvarh > 0
    && (capKvarh / Math.max(1, hoursWhere(rows))) < opts.qStepMinKvar;

  return {
    kind: 'leadingPf',
    title: 'Capacitor bank over-correcting into leading power factor',
    explanation: `The meter's capacitive reactive-energy register advanced ${r1(capKvarh)} kVArh this period — ${pct(share)} of the inductive reactive energy over the same window — and ${pct(offHoursShare)} of that accumulated during non-production hours. That register only advances when the plant is running leading, so the bank is holding capacitance in at light load.${maxVll > 0 ? ` Peak line voltage reached ${r1(maxVll)} V over the same period.` : ''}`,
    action: undersizedStage
      ? `Review the APFC stage sizing rather than the settings — the leading surplus is smaller than the smallest switchable stage, so no control setting can clear it. A smaller trim stage is the usual remedy. Have the APFC vendor confirm against the bank's actual stage schedule.`
      : `Have the APFC controller's target power factor and step-down delay reviewed for the light-load profile, and confirm stages are dropping out overnight and at weekends. A qualified panel contractor should verify contactor drop-out before any setting change.`,
    stats: {
      'leading (capacitive) reactive energy accumulated this period (kVArh)': `${r1(capKvarh)} kVArh`,
      'inductive reactive energy over the same period (kVArh)': `${r1(indKvarh)} kVArh`,
      'leading energy as a share of inductive energy': pct(share),
      'share of the leading energy that accumulated during non-production hours': pct(offHoursShare),
      ...(maxVll > 0 ? { 'highest line voltage recorded this period (V)': `${r1(maxVll)} V` } : {}),
      ...(opts.qStepMinKvar !== undefined ? { 'smallest switchable APFC stage (kVAr)': `${opts.qStepMinKvar} kVAr` } : {}),
    },
    narrativeFacts: [
      'The inductive and capacitive reactive-energy registers are mutually exclusive quadrant counters — the capacitive one physically cannot advance unless the plant was genuinely running leading, so this is measurement rather than inference.',
      'The consequence to lead with is overvoltage stress on the bank and on connected equipment, which applies everywhere. Whether leading power factor also attracts a tariff penalty is state-specific and must be checked against the applicable DISCOM schedule before it is claimed.',
      ...(offHoursShare > 0.5
        ? ['Most of the leading energy accumulated outside production hours, which is the classic signature of stages staying in at light load rather than a control fault during production.']
        : []),
      ...(undersizedStage
        ? ['The surplus is smaller than the smallest stage that can be switched, so this is a bank sizing issue and NOT something a controller setting can fix — do not recommend a settings change alone.']
        : []),
    ],
    score: 18 + Math.min(12, share * 100),
  };
}

// ─── Meter capability gate ────────────────────────────────────────────────────

/**
 * Does this row set come from a meter reporting the LK4410 extended register
 * block — reactive-energy counters, THD and displacement PF?
 *
 * This matters beyond "which features light up". On the LK4405 `varTotal` is
 * not a measurement at all: lk4405.ts derives it as √(S²−P²) from apparent and
 * real power, so it carries no information P and S do not already carry, and a
 * square root cannot distinguish leading from lagging. Any capacitor conclusion
 * drawn from it is arithmetic read back to itself.
 *
 * Deliberately tested on CAPABILITY rather than a model string, so a future
 * meter is judged by what it reports. And deliberately tested across several
 * registers rather than varh alone: a correctly compensated or newly
 * commissioned LK4410 site can sit at zero capacitive energy for weeks, and
 * gating on that one register would silently skip it.
 */
export function hasExtendedRegisterBlock(rows: TelemetryRow[]): boolean {
  return rows.some(r =>
    finite(r.varhIndReceived) !== 0 ||
    finite(r.varhCapReceived) !== 0 ||
    finite(r.vRThdPct) !== 0 ||
    finite(r.vYThdPct) !== 0 ||
    finite(r.vBThdPct) !== 0 ||
    finite(r.displacementPFR) !== 0);
}

// ─── Feature 19 — capacitor fade monitor ──────────────────────────────────────

/** One switchable APFC stage, from the commissioning record. */
export interface CapacitorStage {
  /** Nameplate rating in kVAr. */
  kvar: number;
  /** Detuning reactor percentage, e.g. 7 for a 7% detuned stage. */
  detuningPct?: number;
  /**
   * Where the kVAr figure is quoted. A series detuning reactor raises the
   * voltage across the can, so a stage delivers MORE at the panel terminals
   * than the can is rated for: Q_terminals = Q_can / (1 − p). Getting this
   * wrong on a 7% stage costs ~7.5% of apparent fade — enough to invert the
   * diagnosis, which is why it is a required commissioning datum.
   */
  quotedAt?: 'terminals' | 'can';
}

export interface FadeOptions {
  stages: CapacitorStage[];
  /** Rated line voltage. Default 415 V. */
  vRatedV?: number;
  /** Rated frequency. Default 50 Hz. */
  fRatedHz?: number;
  /** Samples each side of a switching instant used to fit a plateau. Default 4. */
  plateauSamples?: number;
  /** Median fade above which the finding is raised. Default 5%. */
  fadeAlertPct?: number;
  /** Minimum accepted switching events before any fade is reported. Default 3. */
  minEvents?: number;
}

/** Delivered kVAr at the panel terminals for a stage, from its nameplate. */
export function stageRatedKvar(stage: CapacitorStage): number {
  const p = (stage.detuningPct ?? 0) / 100;
  return stage.quotedAt === 'can' && p > 0 && p < 1
    ? stage.kvar / (1 - p)
    : stage.kvar;
}

/** Least-squares line through (x, y), evaluated at x0. */
function fitAt(xs: number[], ys: number[], x0: number): number {
  const n = xs.length;
  if (n === 0) return NaN;
  if (n === 1) return ys[0];
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let sxy = 0, sxx = 0;
  for (let i = 0; i < n; i++) {
    sxy += (xs[i] - mx) * (ys[i] - my);
    sxx += (xs[i] - mx) ** 2;
  }
  const slope = sxx === 0 ? 0 : sxy / sxx;
  return my + slope * (x0 - mx);
}

const mean = (xs: number[]): number =>
  xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;

/**
 * Trustworthy increase between two consecutive readings of a CUMULATIVE
 * register, or null when the step cannot be believed.
 *
 * Rejects a step when either endpoint reads exactly zero. A running cumulative
 * counter does not return to zero, so a zero is a failed read rather than a
 * value — and the dangerous half is the sample AFTER it, not the zero itself.
 *
 * Verified on ralpro-main: every one of the 10 zero readings in its history is
 * an isolated sample, with the counter resuming exactly where it left off.
 *
 *     10:51:46   24,148.3 kVArh
 *     10:56:47        0.0          <- failed read
 *     11:01:47   24,161.7 kVArh    <- resumes, only 13.4 kVArh of real energy
 *
 * Discarding the fall but counting the climb reads that recovery as 24,161
 * kVArh of new energy. Ten such glitches injected roughly 150,000 phantom
 * kVArh into a single weekly report — five times the site's entire recorded
 * accumulation.
 *
 * Negative steps are also rejected, which is what handles a genuine counter
 * reset or a meter swap.
 */
function cumulativeStep(prev: unknown, cur: unknown): number | null {
  const a = Math.abs(finite(prev));
  const b = Math.abs(finite(cur));
  if (a === 0 || b === 0) return null;   // failed read on either side
  const d = b - a;
  return d > 0 ? d : null;               // reset, swap, or no movement
}

export interface FadeEvent {
  at: Date;
  /** Reactive step measured between the two extrapolated plateaus (kVAr). */
  deltaQKvar: number;
  /** Active step across the same instant (kW) — the validity gate. */
  deltaPKw: number;
  vMeasV: number;
  fMeasHz: number;
  /** Matched stage rating at the terminals, or null when identity is ambiguous. */
  stageKvar: number | null;
  fadePct: number | null;
}

/**
 * Detect capacitor switching events and estimate fade against nameplate.
 *
 *   Fade% = [1 − (ΔQ_meas/Q_rated)·(V_rated/V_meas)²·(f_rated/f_meas)] × 100
 *
 * ΔQ is the difference between two SETTLED plateaus, each line-fitted and
 * extrapolated to the switching instant, with the transition sample and its
 * neighbour discarded — a single-sample difference straddles the transient and
 * is not a step measurement. The event is accepted only when |ΔP/ΔQ| < 0.05,
 * which rejects load changes masquerading as switching.
 *
 * Stage identity is NOT recoverable from the meter, so a step is attributed to
 * a stage only when one nameplate is unambiguously closest. Everything else is
 * reported as an unattributed step. The APFC controller link removes this.
 */
export function detectFadeEvents(rows: TelemetryRow[], opts: FadeOptions): FadeEvent[] {
  const stages = opts.stages ?? [];
  if (!stages.length) return [];

  const k = opts.plateauSamples ?? 4;
  const vRated = opts.vRatedV ?? 415;
  const fRated = opts.fRatedHz ?? 50;

  const rated = stages.map(stageRatedKvar).filter(q => q > 0).sort((a, b) => a - b);
  if (!rated.length) return [];
  // A step must be at least 40% of the smallest stage to be a switching event.
  const minStep = rated[0] * 0.4;

  const sorted = [...rows]
    .filter(r => !r.isDropout)
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  const t = sorted.map(r => r.timestamp.getTime() / 1000);
  const q = sorted.map(r => finite(r.varTotal) / 1000);   // VAr → kVAr
  const p = sorted.map(r => finite(r.wattsTotal) / 1000); // W  → kW

  const events: FadeEvent[] = [];

  for (let i = k; i < sorted.length - k - 2; i++) {
    if (Math.abs(q[i + 1] - q[i]) < minStep) continue;

    // Discard sample i (the transition) and i+1 (its neighbour).
    const bi = [];
    for (let j = i - k; j <= i - 1; j++) bi.push(j);
    const ai = [];
    for (let j = i + 2; j <= i + 1 + k; j++) ai.push(j);
    if (bi[0] < 0 || ai[ai.length - 1] >= sorted.length) continue;

    const tsw = (t[i] + t[i + 1]) / 2;
    const qBefore = fitAt(bi.map(j => t[j]), bi.map(j => q[j]), tsw);
    const qAfter = fitAt(ai.map(j => t[j]), ai.map(j => q[j]), tsw);
    const pBefore = fitAt(bi.map(j => t[j]), bi.map(j => p[j]), tsw);
    const pAfter = fitAt(ai.map(j => t[j]), ai.map(j => p[j]), tsw);
    if (!Number.isFinite(qBefore) || !Number.isFinite(qAfter)) continue;

    const dQ = Math.abs(qAfter - qBefore);
    const dP = Math.abs(pAfter - pBefore);
    if (dQ < minStep) continue;
    if (dQ > 0 && dP / dQ >= 0.05) continue;   // validity gate — load change, not switching

    const vMeas = mean(ai.map(j => finite(sorted[j].vllAvg))) || vRated;
    const fMeas = mean(ai.map(j => finite(sorted[j].frequencyHz)).filter(f => f > 0)) || fRated;

    // Attribute to a stage only when one nameplate is unambiguously closest.
    let stageKvar: number | null = null;
    const byDistance = [...rated].sort((a, b) => Math.abs(dQ - a) - Math.abs(dQ - b));
    const best = byDistance[0];
    const next = byDistance[1];
    const closeEnough = Math.abs(dQ - best) < 0.35 * best;
    const distinct = next === undefined || Math.abs(next - best) > 0.2 * best;
    if (closeEnough && distinct) stageKvar = best;

    const fadePct = stageKvar
      ? (1 - (dQ / stageKvar) * (vRated / vMeas) ** 2 * (fRated / fMeas)) * 100
      : null;

    events.push({
      at: sorted[i].timestamp,
      deltaQKvar: dQ,
      deltaPKw: dP,
      vMeasV: vMeas,
      fMeasHz: fMeas,
      stageKvar,
      fadePct,
    });

    i += k + 1;   // don't re-detect the same transition from inside its own plateau
  }

  return events;
}

export function capacitorFadeFinding(
  rows: TelemetryRow[],
  opts: FadeOptions,
): ScoredFinding | null {
  if (!opts.stages?.length) return null;
  // varTotal is derived arithmetic on the LK4405 — never run a fade estimate
  // against it, however the stages happen to be configured.
  if (!hasExtendedRegisterBlock(rows)) return null;
  const events = detectFadeEvents(rows, opts);
  if (!events.length) return null;

  const minEvents = opts.minEvents ?? 3;
  const alertPct = opts.fadeAlertPct ?? 5;

  // Fade outside this band is a mis-read nameplate or a mis-attributed stage,
  // not a degraded capacitor — do not average it in.
  const fades = events
    .map(e => e.fadePct)
    .filter((f): f is number => f !== null && f > -20 && f < 100);

  if (fades.length < minEvents) return null;

  const medianFade = percentileOf(fades, 50);
  if (medianFade < alertPct) return null;

  const matched = events.filter(e => e.stageKvar !== null);
  const worst = [...fades].sort((a, b) => b - a)[0];
  const meanStep = mean(matched.map(e => e.deltaQKvar));
  const meanRated = mean(matched.map(e => e.stageKvar!));

  const r1 = (n: number) => n.toFixed(1);

  return {
    kind: 'capacitorFade',
    title: 'Capacitor stages delivering below nameplate',
    explanation: `Across ${events.length} capacitor switching events this period, ${fades.length} could be matched to a stage rating. Those stages delivered a median of ${r1(medianFade)}% below nameplate once corrected to rated voltage and frequency — an average measured step of ${r1(meanStep)} kVAr against ${r1(meanRated)} kVAr rated, with the worst event ${r1(worst)}% down.`,
    action: `Have the APFC panel's capacitor stages checked by a qualified contractor — measure delivered kVAr stage by stage against nameplate, and inspect for blown internal fuses, dried elements and loose terminations. Confirm the nameplate ratings and detuning percentage on the commissioning record at the same time, since a mis-recorded rating produces this same reading.`,
    stats: {
      'number of capacitor switching events detected this period': String(events.length),
      'events that could be matched to a specific stage rating': String(fades.length),
      'median fade below nameplate across matched events': `${r1(medianFade)}%`,
      'the worst single-event fade measured': `${r1(worst)}%`,
      'average measured reactive step (kVAr)': `${r1(meanStep)} kVAr`,
      'average nameplate rating of the matched stages (kVAr)': `${r1(meanRated)} kVAr`,
      'the fade threshold above which this is reported': `${alertPct}%`,
    },
    narrativeFacts: [
      'Each step is measured between two settled plateaus extrapolated to the switching instant, not between two adjacent readings, and only accepted when the active-power step is under 5% of the reactive step — so a load change cannot be mistaken for a capacitor switching in.',
      'The figure is already corrected to rated voltage and frequency, so it is not an artefact of the bus running low or the grid running off-frequency.',
      'Stage identity is inferred from step size, which only works where stage ratings are distinct. This names how much capacity is missing, NOT which physical stage is at fault — do not attribute it to a numbered stage.',
      'A nameplate recorded at the capacitor can rather than at the panel terminals shifts this figure by roughly the detuning percentage, so confirm the commissioning record before ordering replacements.',
    ],
    score: 20 + Math.min(15, medianFade),
  };
}

// ─── Feature 13 — utilization tracker ─────────────────────────────────────────

export interface UtilizationOptions {
  /** Idle draw when energised but not working (kW). Estimated if omitted. */
  pIdleKw?: number;
  /** Utilisation below this is worth reporting. Default 0.7. */
  alertBelow?: number;
  minSamples?: number;
}

/**
 * Separates "energised" from "actually working". A machine drawing its idle
 * load for most of the shift is paying for availability it is not using, which
 * is invisible on an energy total but obvious on this ratio.
 *
 * P_idle is a short commissioning observation, not a baseline window. When it
 * is not supplied it is estimated as the 10th percentile of energised load and
 * the finding says so, because an estimated idle floor on a machine that is
 * genuinely busy will read as high utilisation rather than low.
 */
export function utilizationFinding(
  rows: TelemetryRow[],
  opts: UtilizationOptions = {},
): ScoredFinding | null {
  const minSamples = opts.minSamples ?? 100;
  const alertBelow = opts.alertBelow ?? 0.7;

  const live = rows.filter(r => !r.isDropout);
  if (live.length < minSamples) return null;

  const kw = live.map(r => finite(r.kwTotal));
  const peak = percentileOf(kw, 99);
  if (peak <= 0) return null;

  const onThreshold = Math.max(0.5, 0.02 * peak);
  const energised = live.filter(r => finite(r.kwTotal) > onThreshold);
  if (energised.length < minSamples) return null;

  const estimated = opts.pIdleKw === undefined;
  const idleKw = opts.pIdleKw ?? percentileOf(energised.map(r => finite(r.kwTotal)), 10);
  if (idleKw <= 0) return null;

  const working = energised.filter(r => finite(r.kwTotal) > idleKw * 1.15);
  const utilization = working.length / energised.length;
  if (utilization >= alertBelow) return null;

  const energisedH = hoursWhere(live, r => finite(r.kwTotal) > onThreshold);
  const workingH = hoursWhere(live, r => finite(r.kwTotal) > idleKw * 1.15);
  const idleH = Math.max(0, energisedH - workingH);

  // The rpm register materially sharpens running-vs-working on rotating plant.
  const rpmRows = energised.filter(r => finite(r.rpm) > 0);
  const rpmAvailable = rpmRows.length > energised.length * 0.5;

  const r1 = (n: number) => n.toFixed(1);
  const pct = (n: number) => `${(n * 100).toFixed(0)}%`;

  return {
    kind: 'utilization',
    title: 'Plant energised well beyond the hours it is working',
    explanation: `The plant drew power for ${r1(energisedH)} hours this period but was above its idle load for only ${r1(workingH)} of them — ${pct(utilization)} utilisation, leaving ${r1(idleH)} hours energised at or near idle${estimated ? ' (idle load estimated from the data, not yet confirmed on site)' : ''}.`,
    action: `Review start-up and shut-down practice against the shift pattern — plant left energised between jobs is the usual cause. Confirm the idle load figure on site before acting on the split${estimated ? ', since it is currently estimated rather than measured at commissioning' : ''}.`,
    stats: {
      'hours the plant was drawing power this period': r1(energisedH),
      'hours it was above idle load, i.e. actually working': r1(workingH),
      'hours energised but sitting at or near idle': r1(idleH),
      'utilisation, working hours over energised hours': pct(utilization),
      [`idle load used for the split (kW)${estimated ? ', estimated' : ', from commissioning'}`]: `${r1(idleKw)} kW`,
      'peak load this period (kW)': `${r1(peak)} kW`,
    },
    narrativeFacts: [
      estimated
        ? 'The idle load here is ESTIMATED from the data as the 10th percentile of energised load, not measured at commissioning — say so, and treat the split as indicative until it is confirmed on site.'
        : 'The idle load used here was measured at commissioning, so the working/idle split is a measurement rather than an estimate.',
      ...(rpmAvailable
        ? ['The meter is also reporting shaft speed for this plant, which can separate turning from working more precisely than power alone on rotating equipment.']
        : []),
      'This is about hours, not energy — a low utilisation figure can sit alongside a perfectly normal energy total, which is exactly why it is worth reporting separately.',
    ],
    score: 10 + Math.min(10, (1 - utilization) * 20),
  };
}

// ─── Feature 12 — power factor billing basis ──────────────────────────────────

export interface BilledPfResult {
  /** PF the DISCOM bills on: cumulative kWh / kVAh across the period. */
  pfEnergy: number;
  /** Mean of the instantaneous PF readings — what the live alert uses. */
  pfInstantMean: number;
  kwh: number;
  kvah: number;
}

/**
 * Most Indian tariffs assess power factor from kWh/kVAh over the billing
 * period — the ENERGY REGISTER RATIO, not an average of instantaneous PF
 * readings. The two diverge whenever load varies, because the instantaneous
 * mean weights every sample equally while the billed figure weights by energy.
 *
 * Deltas are summed step-by-step with negative steps discarded, so a counter
 * reset cannot corrupt the ratio.
 */
export function billedPowerFactor(rows: TelemetryRow[]): BilledPfResult | null {
  const sorted = [...rows]
    .filter(r => !r.isDropout)
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  if (sorted.length < 2) return null;

  let wh = 0, vah = 0;
  for (let i = 1; i < sorted.length; i++) {
    // Same glitch guard as the reactive counters — a zero reading on an energy
    // register is a failed read, and counting the recovery from one inflates
    // the total by a whole counter's worth. Taparia reports zero on ~83% of
    // its energy samples, so this path is exercised heavily there.
    const dW = cumulativeStep(sorted[i - 1].whReceived, sorted[i].whReceived);
    const dV = cumulativeStep(sorted[i - 1].vahReceived, sorted[i].vahReceived);
    if (dW !== null) wh += dW;
    if (dV !== null) vah += dV;
  }
  if (wh <= 0 || vah <= 0) return null;

  const valid = sorted.filter(r => r.pfValid && finite(r.pfAvg) > 0).map(r => finite(r.pfAvg));
  if (!valid.length) return null;

  return {
    pfEnergy: wh / vah,
    pfInstantMean: mean(valid),
    kwh: wh / 1000,
    kvah: vah / 1000,
  };
}

/**
 * Raised when the billed PF basis and the instantaneous basis disagree enough
 * to change what the site is charged. This does NOT alter any bill figure — it
 * reports the divergence so the basis can be reconciled deliberately.
 */
export function pfBasisFinding(
  rows: TelemetryRow[],
  minDivergence = 0.02,
): ScoredFinding | null {
  const r = billedPowerFactor(rows);
  if (!r) return null;

  const divergence = r.pfInstantMean - r.pfEnergy;
  if (Math.abs(divergence) < minDivergence) return null;

  const r3 = (n: number) => n.toFixed(3);
  const r0 = (n: number) => Math.round(n).toLocaleString('en-IN');
  const optimistic = divergence > 0;

  return {
    kind: 'pfBasis',
    title: 'Billed and displayed power factor disagree',
    explanation: `Measured from the energy registers — ${r0(r.kwh)} kWh against ${r0(r.kvah)} kVAh — power factor for this period is ${r3(r.pfEnergy)}. The average of the instantaneous readings is ${r3(r.pfInstantMean)}, a difference of ${r3(Math.abs(divergence))}. Most Indian tariffs assess power factor on the energy-register ratio, so the ${optimistic ? 'higher displayed figure is the more optimistic of the two' : 'displayed figure is the more conservative of the two'}.`,
    action: `Confirm from the applicable DISCOM tariff schedule which basis power factor is assessed on for this connection, and reconcile the dashboard's penalty calculation to match it. No bill figure has been changed on the strength of this finding.`,
    stats: {
      'power factor from the energy registers, kWh over kVAh': r3(r.pfEnergy),
      'mean of the instantaneous power factor readings': r3(r.pfInstantMean),
      'the difference between the two bases': r3(Math.abs(divergence)),
      'active energy over the period (kWh)': `${r0(r.kwh)} kWh`,
      'apparent energy over the period (kVAh)': `${r0(r.kvah)} kVAh`,
    },
    narrativeFacts: [
      'The two figures differ because the instantaneous mean weights every reading equally, while the billed ratio weights by energy — so hours at heavy load count for more in the billed figure.',
      'Do NOT present either number as the penalty position until the tariff basis is confirmed for this connection. This finding reports a discrepancy; it does not resolve it.',
      optimistic
        ? 'The displayed figure is the more flattering one here, so the billed position may be worse than the dashboard currently suggests.'
        : 'The billed figure is the more flattering one here, so the dashboard is currently the conservative view.',
    ],
    score: 14 + Math.min(10, Math.abs(divergence) * 200),
  };
}

// ─── Feature 8 (aggregate half) — voltage THD against IEEE 519 ────────────────

/**
 * IEEE 519 voltage-THD limits, by the nominal voltage of the bus being
 * measured. Table 1 of IEEE 519-2014/2022:
 *
 *        V ≤ 1 kV            8.0%
 *    1 kV < V ≤ 69 kV        5.0%
 *   69 kV < V ≤ 161 kV       2.5%
 *        V > 161 kV          1.5%
 *
 * IMPORTANT: this is the limit for the bus WE MEASURE, which on this fleet is
 * the LV side (~415 V line-to-line). The utility connection is HT — 33 kV at
 * ralpro — where the limit is 5%. We do not measure there, so nothing here
 * should be presented as compliance at the point of common coupling.
 */
export function ieee519VthdLimitPct(nominalVllVolts: number): number {
  if (nominalVllVolts <= 1_000) return 8;
  if (nominalVllVolts <= 69_000) return 5;
  if (nominalVllVolts <= 161_000) return 2.5;
  return 1.5;
}

/** Limit for an LV bus. Retained for callers that already assume ≤1 kV. */
export const IEEE519_VTHD_LIMIT_PCT = 8;

/**
 * The aggregate voltage-THD half of Feature 8, which the scalar THD registers
 * fully support. The individual-harmonic limit (5% per order) is NOT evaluated
 * here and must not be claimed — it needs a per-order spectrum the meter does
 * not expose. That caveat is carried in narrativeFacts so the narration cannot
 * quietly widen the claim.
 */
export function voltageThdFinding(rows: TelemetryRow[]): ScoredFinding | null {
  const eligible = rows.filter(r => !r.isDropout);
  const limit = ieee519VthdLimitPct(
    percentileOf(eligible.map(r => finite(r.vllAvg)).filter(v => v > 0), 50),
  );
  const phases = [
    { name: 'R', values: eligible.map(r => finite(r.vRThdPct)).filter(v => v > 0) },
    { name: 'Y', values: eligible.map(r => finite(r.vYThdPct)).filter(v => v > 0) },
    { name: 'B', values: eligible.map(r => finite(r.vBThdPct)).filter(v => v > 0) },
  ].filter(p => p.values.length > 0);

  if (!phases.length) return null;   // LK4405 site — THD not reported

  const stats = phases.map(p => ({
    name: p.name,
    mean: p.values.reduce((a, b) => a + b, 0) / p.values.length,
    p95: percentileOf(p.values, 95),
    max: Math.max(...p.values),
    pctOver: p.values.filter(v => v > limit).length / p.values.length,
  })).sort((a, b) => b.p95 - a.p95);

  const worst = stats[0];
  if (worst.p95 <= limit && worst.pctOver < 0.05) return null;

  const r1 = (n: number) => n.toFixed(1);
  const pct = (n: number) => `${(n * 100).toFixed(0)}%`;

  return {
    kind: 'voltageThd',
    title: 'Voltage distortion above the IEEE 519 limit',
    explanation: `Phase ${worst.name} carried the worst voltage distortion this period, averaging ${r1(worst.mean)}% THD and reaching ${r1(worst.max)}% at its peak, with ${pct(worst.pctOver)} of readings above the ${limit}% IEEE 519 limit for a bus at this voltage. Its 95th-percentile reading was ${r1(worst.p95)}%.`,
    action: `Have a power-quality survey identify which feeders carry the non-linear load — variable-speed drives, rectifiers and switched-mode supplies are the usual contributors. Filter sizing should follow the survey, not precede it.`,
    stats: {
      'the phase with the worst voltage distortion': `Phase ${worst.name}`,
      'mean voltage THD on that phase this period': `${r1(worst.mean)}%`,
      'highest voltage THD reading on that phase': `${r1(worst.max)}%`,
      '95th-percentile voltage THD on that phase': `${r1(worst.p95)}%`,
      'percentage of readings above the IEEE 519 limit': pct(worst.pctOver),
      'the IEEE 519 voltage THD limit applied': `${limit}%`,
      ...Object.fromEntries(stats.slice(1).map(s =>
        [`Phase ${s.name} mean voltage THD, for comparison`, `${r1(s.mean)}%`])),
    },
    narrativeFacts: [
      'This is the AGGREGATE voltage-THD limit only. Do NOT state or imply that the site is compliant or non-compliant with the individual-harmonic limits, or with current TDD — those need a per-order harmonic spectrum, which this meter does not measure.',
      'Voltage distortion is a bus-wide condition, so it affects every load on the same bus, not only the equipment that causes it.',
    ],
    score: 15 + Math.min(15, worst.p95),
  };
}

// ─── Chart series ─────────────────────────────────────────────────────────────
//
// The detectors above answer a yes/no question over one window and return a
// sentence. A chart needs a series. These produce the day-by-day figures behind
// each verdict, so the dashboard and the weekly report cannot drift apart —
// they share the same arithmetic.
//
// All are pure, all return [] rather than throwing when the registers are
// absent, and all bucket by SHIFT DAY rather than calendar day: a reading at
// 02:00 belongs to the night that started the previous evening, which is the
// only grouping that makes an off-hours floor mean anything.

/** Hours subtracted before bucketing, so a night maps to the day it began. */
const SHIFT_OFFSET_H = 6;

/** Local YYYY-MM-DD of the shift a reading belongs to. */
export function shiftDayKey(ts: Date, offsetHours = SHIFT_OFFSET_H): string {
  const d = new Date(ts.getTime() - offsetHours * 3_600_000);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return d.getFullYear() + '-' + m + '-' + day;
}

/** Instant the shift day containing `ts` ends — 06:00 the next morning. */
function shiftDayEnd(ts: Date): number {
  const d = new Date(ts.getTime() - SHIFT_OFFSET_H * 3_600_000);
  d.setHours(0, 0, 0, 0);
  return d.getTime() + (SHIFT_OFFSET_H + 24) * 3_600_000;
}

function groupByShiftDay(rows: TelemetryRow[]): Map<string, TelemetryRow[]> {
  const out = new Map<string, TelemetryRow[]>();
  for (const r of rows) {
    const k = shiftDayKey(r.timestamp);
    const bucket = out.get(k);
    if (bucket) bucket.push(r); else out.set(k, [r]);
  }
  return out;
}

// ── Feature 24 — off-hours floor per night ───────────────────────────────────

export interface StandbyPoint {
  /** Shift day, YYYY-MM-DD. */
  day: string;
  /** P5 of load during that night's non-production hours (kW). */
  floorKw: number;
  /** P99 of load across the whole shift day, for contrast (kW). */
  peakKw: number;
  /** Non-production hours actually measured that night. */
  offHours: number;
  /** Addressable load above the agreed essential base, when one is set (kW). */
  addressableKw: number | null;
}

/**
 * One point per night, so the trend is visible. A single week's figure says
 * what the floor is; this says whether it is moving, which is the only version
 * anyone can act on.
 */
export function standbySeries(
  rows: TelemetryRow[],
  opts: { nonProdWindows?: NonProdWindow[]; pEssentialKw?: number; minSamples?: number } = {},
): StandbyPoint[] {
  const windows = opts.nonProdWindows ?? DEFAULT_NON_PROD_WINDOWS;
  const minSamples = opts.minSamples ?? 12;
  const live = rows.filter(r => !r.isDropout);
  const out: StandbyPoint[] = [];

  for (const [day, dayRows] of groupByShiftDay(live)) {
    const off = dayRows.filter(r => inNonProdWindow(r.timestamp, windows));
    if (off.length < minSamples) continue;   // partial night — not comparable

    // The window almost always starts and ends mid-shift, leaving a stub bucket
    // at each end. Their floor may be sound but their peak is computed from a
    // few hours, so plotting them puts a misleading first and last point on the
    // chart. Require half a day of coverage before a point is comparable.
    if (hoursWhere(dayRows) < 12) continue;

    const floorKw = percentileOf(off.map(r => finite(r.kwTotal)), 5);
    const peakKw = percentileOf(dayRows.map(r => finite(r.kwTotal)), 99);
    const offHours = hoursWhere(dayRows, r => inNonProdWindow(r.timestamp, windows));

    out.push({
      day, floorKw, peakKw, offHours,
      addressableKw: opts.pEssentialKw !== undefined
        ? Math.max(0, floorKw - opts.pEssentialKw)
        : null,
    });
  }
  return out.sort((a, b) => a.day.localeCompare(b.day));
}

// ── Feature 22 — leading reactive energy per day ─────────────────────────────

export interface LeadingPfPoint {
  day: string;
  /** Capacitive (leading) reactive energy accrued that day, kVArh. */
  capKvarh: number;
  /** The share of it that fell inside non-production hours, kVArh. */
  capOffHoursKvarh: number;
  /** Inductive reactive energy the same day, for scale. */
  indKvarh: number;
  /** Running total of capacitive energy across the window. */
  cumulativeKvarh: number;
}

/**
 * On a correctly compensated plant this series is flat at zero — the register
 * physically cannot advance unless the plant ran leading. A rising line is the
 * finding, with no interpretation needed.
 *
 * Uses the same cumulativeStep() guard as the detector, so a failed meter read
 * cannot inject a phantom day-sized spike.
 */
export function leadingPfSeries(rows: TelemetryRow[]): LeadingPfPoint[] {
  const sorted = [...rows]
    .filter(r => !r.isDropout)
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  if (sorted.length < 2) return [];
  if (!sorted.some(r => finite(r.varhCapReceived) !== 0)) return [];   // LK4405

  const byDay = new Map<string, { cap: number; capOff: number; ind: number }>();
  for (let i = 1; i < sorted.length; i++) {
    const day = shiftDayKey(sorted[i].timestamp);
    const acc = byDay.get(day) ?? { cap: 0, capOff: 0, ind: 0 };

    const dCap = cumulativeStep(sorted[i - 1].varhCapReceived, sorted[i].varhCapReceived);
    const dInd = cumulativeStep(sorted[i - 1].varhIndReceived, sorted[i].varhIndReceived);
    if (dCap !== null) {
      acc.cap += dCap / 1000;
      if (inNonProdWindow(sorted[i].timestamp, DEFAULT_NON_PROD_WINDOWS)) acc.capOff += dCap / 1000;
    }
    if (dInd !== null) acc.ind += dInd / 1000;
    byDay.set(day, acc);
  }

  let running = 0;
  return [...byDay.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([day, v]) => {
      running += v.cap;
      return {
        day,
        capKvarh: v.cap,
        capOffHoursKvarh: v.capOff,
        indKvarh: v.ind,
        cumulativeKvarh: running,
      };
    });
}

// ── Feature 8 — voltage THD per day against the limit ────────────────────────

export interface ThdPoint {
  day: string;
  /** 95th-percentile voltage THD that day, per phase (%). */
  vR: number;
  vY: number;
  vB: number;
  /** Worst phase that day, for the headline (%). */
  worst: number;
  /** The IEEE 519 limit, carried so the chart can draw it without knowing it. */
  limit: number;
}

/**
 * The 95th percentile rather than the mean, because a limit chart should show
 * what the site is close to breaching, not what it averages. Returns [] on a
 * meter that does not report THD.
 */
export function thdSeries(rows: TelemetryRow[]): ThdPoint[] {
  const live = rows.filter(r => !r.isDropout);
  if (!live.some(r => finite(r.vRThdPct) !== 0)) return [];   // LK4405

  // The limit depends on the voltage of the bus we are measuring, so derive it
  // from the readings rather than assuming LV.
  const limit = ieee519VthdLimitPct(
    percentileOf(live.map(r => finite(r.vllAvg)).filter(v => v > 0), 50),
  );

  const out: ThdPoint[] = [];
  for (const [day, dayRows] of groupByShiftDay(live)) {
    const p = (sel: (r: TelemetryRow) => number) => {
      const vals = dayRows.map(sel).map(finite).filter(v => v > 0);
      return vals.length ? percentileOf(vals, 95) : 0;
    };
    const vR = p(r => r.vRThdPct), vY = p(r => r.vYThdPct), vB = p(r => r.vBThdPct);
    if (vR === 0 && vY === 0 && vB === 0) continue;
    out.push({ day, vR, vY, vB, worst: Math.max(vR, vY, vB), limit });
  }
  return out.sort((a, b) => a.day.localeCompare(b.day));
}

// ── Feature 13 — working vs idle hours per day ───────────────────────────────

export interface UtilizationPoint {
  day: string;
  /** Hours above the idle threshold — actually producing. */
  workingH: number;
  /** Hours drawing power but at or near idle. */
  idleH: number;
  /** Hours effectively off. */
  offH: number;
  /** working / (working + idle), or null when the plant never ran that day. */
  utilization: number | null;
}

/**
 * The idle threshold is a single figure for the whole window rather than
 * per-day, otherwise a quiet day redefines "idle" downward and reports itself
 * as busy. Pass pIdleKw from commissioning where it exists; otherwise it is
 * estimated once, across the whole window, from the 10th percentile of
 * energised load.
 */
export function utilizationSeries(
  rows: TelemetryRow[],
  opts: { pIdleKw?: number } = {},
): UtilizationPoint[] {
  const live = rows.filter(r => !r.isDropout);
  if (live.length < 2) return [];

  const peak = percentileOf(live.map(r => finite(r.kwTotal)), 99);
  if (peak <= 0) return [];
  const onThreshold = Math.max(0.5, 0.02 * peak);

  const energised = live.filter(r => finite(r.kwTotal) > onThreshold);
  if (!energised.length) return [];

  const idleKw = opts.pIdleKw
    ?? percentileOf(energised.map(r => finite(r.kwTotal)), 10);
  const out: UtilizationPoint[] = [];
  for (const [day, dayRows] of groupByShiftDay(live)) {
    const workingH = hoursWhere(dayRows, r => finite(r.kwTotal) > Math.max(onThreshold, idleKw * 1.15));
    const idleH = hoursWhere(dayRows, r => {
      const kw = finite(r.kwTotal);
      return kw > onThreshold && kw <= idleKw * 1.15;
    });
    const offH = hoursWhere(dayRows, r => finite(r.kwTotal) <= onThreshold);
    const ran = workingH + idleH;
    out.push({
      day,
      workingH,
      idleH,
      offH,
      utilization: ran > 0 ? workingH / ran : null,
    });
  }
  return out.sort((a, b) => a.day.localeCompare(b.day));
}
